import { SelectProps } from 'antd';
import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import useSelectOptions from '@/hooks/useSelectOptions';
import {
  Model,
  ModelsRequest,
} from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.types';
import {
  useModelMutation,
  useModelsFilter,
} from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: ModelsRequest;
  queryEnable?: boolean;
  onModelSelect?: (model?: Model) => void;
};

const ModelsInfiniteSelect: React.FC<Props> = ({
  queryFilter,
  queryEnable = false,
  onModelSelect,
  ...props
}) => {
  const [selectClicked, setSelectClicked] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);

  const modelsList = useModelsFilter(
    {
      name: search,
      ...queryFilter,
    },
    (queryEnable || selectClicked) && !props.disabled,
  );

  const models = flatInfiniteData(modelsList.data);

  const modelDetail = useModelMutation();

  const options = useSelectOptions({
    list: modelsList,
    detail: modelDetail,
    selectProps: props,
    labelFormatter: (modal) => modal.name,
  });

  useEffect(() => {
    if (
      modelDetail.data?.id &&
      modelDetail.data.id === props.value &&
      !!onModelSelect
    ) {
      onModelSelect(modelDetail.data);
    }
  }, [modelDetail.data, props.value]);

  return (
    <InfiniteSelect
      fetchNextPage={modelsList.fetchNextPage}
      hasNextPage={modelsList.hasNextPage}
      loading={modelsList.isFetching}
      options={options}
      onSelect={(modelId: number) => {
        setSelectClicked(false);

        const selectedModel = models.find((model) => model.id === modelId);

        if (!!selectedModel && !!onModelSelect) {
          onModelSelect(selectedModel);
        }
      }}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      {...props}
    />
  );
};

export default ModelsInfiniteSelect;
